import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Link2Off, Loader2 } from 'lucide-react';
import { api, ApiRequestError } from '@/lib/api-client';
import { getMockPublicSession } from '@/lib/mock-public-session';
import { CheckoutShell } from './CheckoutShell';
import { StatusChip } from './StatusChip';

const REDIRECT_DELAY_SECONDS = 3;
const SUCCESS_POLL_MS = 5000;
const SUCCESS_POLL_STATUSES = new Set(['pending', 'rail_chosen', 'payment_detected']);
// Whop has historically taken anywhere from a few seconds to a minute or two
// to fire its webhook. We additionally reconcile via the Whop API at a slower
// cadence so we eventually settle even if the webhook is dropped, slow, or
// arrives after the session has been auto-expired.
const RECONCILE_INTERVAL_MS = 8000;
// Cap reconcile attempts so we don't poll Whop forever on a session the user
// has abandoned. ~60 attempts × 8s ≈ 8 minutes — well beyond the median Whop
// webhook latency and long enough to revive a recently-expired session.
const RECONCILE_MAX_ATTEMPTS = 60;

interface PublicSession {
  sessionId: string;
  tenantName?: string;
  amountUsdCents: number;
  chosenRail?: string;
  status: string;
  expiresAt: number;
  successRedirectUrl?: string;
  fulfillmentStatus?: string;
  webhookDeliveryStatus?: string;
  settledAt?: number;
  txSignature?: string;
}

function checkoutComplete(session: PublicSession | null): boolean {
  if (!session || session.status.toLowerCase() !== 'settled') return false;
  const fulfillment = session.fulfillmentStatus ?? 'delivered';
  return fulfillment === 'delivered' || fulfillment === 'not_required';
}

function fulfillmentPending(session: PublicSession | null): boolean {
  return Boolean(session && session.status.toLowerCase() === 'settled' && session.fulfillmentStatus === 'pending');
}

function fulfillmentFailed(session: PublicSession | null): boolean {
  return Boolean(session && session.status.toLowerCase() === 'settled' && session.fulfillmentStatus === 'failed');
}

function shouldPollSuccess(session: PublicSession | null): boolean {
  if (!session || checkoutComplete(session)) return false;
  const status = (session.status ?? '').toLowerCase();
  if (status === 'settled') return session.fulfillmentStatus === 'pending';
  return SUCCESS_POLL_STATUSES.has(status);
}

function getWhopPaymentIdFromSearch(search: string): string | null {
  if (!search) return null;
  try {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const candidate = params.get('payment_id')
      ?? params.get('paymentId')
      ?? params.get('receipt_id')
      ?? params.get('receiptId');
    return candidate ? candidate.trim() || null : null;
  } catch {
    return null;
  }
}

export const SuccessPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState<PublicSession | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(REDIRECT_DELAY_SECONDS);
  const reconcileInFlightRef = useRef(false);
  const reconcileAbortedRef = useRef(false);
  const reconcileAttemptsRef = useRef(0);

  const whopPaymentId = getWhopPaymentIdFromSearch(location.search);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let timer: number | null = null;

    const load = async () => {
      try {
        const data = await api.get(`/api/v1/sessions/${sessionId}/public`);
        if (cancelled) return;
        setNotFound(false);
        setLoadError(null);
        setSession(data);
        if (data.status === 'expired' || data.status === 'failed') {
          // Whop sometimes finalizes after the session expires. If we have a
          // payment_id from Whop, the reconcile effect will try to settle the
          // expired session before sending the user to the expired page.
          if (!whopPaymentId || reconcileAbortedRef.current) {
            navigate(`/pay/${sessionId}/expired`, { replace: true });
            return;
          }
        }
        if (shouldPollSuccess(data)) {
          timer = window.setTimeout(load, SUCCESS_POLL_MS);
        }
      } catch (error) {
        const fallback = await getMockPublicSession(sessionId);
        if (cancelled) return;
        if (fallback) {
          setNotFound(false);
          setLoadError(null);
          setSession(fallback);
          if (shouldPollSuccess(fallback)) {
            timer = window.setTimeout(load, SUCCESS_POLL_MS);
          }
          return;
        }
        // No session and no fallback: do not render a premature "Payment
        // confirmed" success screen. A 404 means the session id is invalid
        // or expired; anything else is a transient load failure.
        if (error instanceof ApiRequestError && error.status === 404) {
          // Expected, fully-handled state: the session id is invalid or
          // expired and we render the explicit not-found screen. Do not log a
          // console error for it — that is normal operation, not a fault.
          setNotFound(true);
          setLoadError(null);
        } else {
          setLoadError(
            error instanceof Error && error.message
              ? error.message
              : 'We could not load this payment. Try again in a moment.',
          );
          console.error(error);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [navigate, sessionId, whopPaymentId]);

  // Reconcile against the Whop API at a slow cadence as long as we have a
  // `payment_id` from the return URL and the session hasn't reached a
  // terminal state. This is the fallback path when the asynchronous webhook
  // is late, dropped, or signed against a stale secret.
  useEffect(() => {
    if (!sessionId || !whopPaymentId) return;
    if (!session) return;
    if (session.status === 'settled') return;
    if (reconcileAbortedRef.current) return;

    let cancelled = false;
    let timer: number | null = null;

    const reconcile = async () => {
      if (reconcileInFlightRef.current) {
        timer = window.setTimeout(reconcile, RECONCILE_INTERVAL_MS);
        return;
      }
      if (reconcileAttemptsRef.current >= RECONCILE_MAX_ATTEMPTS) {
        reconcileAbortedRef.current = true;
        return;
      }
      reconcileInFlightRef.current = true;
      reconcileAttemptsRef.current += 1;
      try {
        const data = await api.post(`/api/v1/sessions/${sessionId}/whop-reconcile`, {
          paymentId: whopPaymentId,
        });
        if (cancelled) return;
        if (data?.status === 'settled') {
          setSession((prev) => prev ? { ...prev, status: 'settled' } : prev);
          return;
        }
      } catch (error) {
        if (cancelled) return;
        // 404 means Whop doesn't know that payment id yet — keep polling.
        // 400 (session mismatch / paymentId missing) means we should stop
        // hammering the endpoint; the visible state machine will explain.
        if (error instanceof ApiRequestError && error.status === 400) {
          reconcileAbortedRef.current = true;
          return;
        }
        console.warn('[whop-reconcile] retry', error);
      } finally {
        reconcileInFlightRef.current = false;
      }
      if (!cancelled) {
        timer = window.setTimeout(reconcile, RECONCILE_INTERVAL_MS);
      }
    };

    reconcile();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [sessionId, whopPaymentId, session?.status]);

  const shouldRedirect = checkoutComplete(session) && Boolean(session?.successRedirectUrl);

  useEffect(() => {
    if (!shouldRedirect || !session?.successRedirectUrl) return;
    if (countdown <= 0) {
      window.location.assign(session.successRedirectUrl);
      return;
    }
    const id = window.setTimeout(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [shouldRedirect, session?.successRedirectUrl, countdown]);

  useEffect(() => {
    setCountdown(REDIRECT_DELAY_SECONDS);
  }, [shouldRedirect]);

  const usdStr = session ? (session.amountUsdCents / 100).toFixed(2) : '—';
  const isComplete = checkoutComplete(session);
  const isProcessing = Boolean(session) && !isComplete;
  const settledAt = (session as any)?.settledAt;
  const settledTime = settledAt
    ? new Date(settledAt * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })
    : null;

  if (notFound) {
    return (
      <CheckoutShell>
        <div className='flex-1 flex items-center justify-center p-6'>
          <div
            className='w-full max-w-md rounded-xl border p-8 flex flex-col items-center gap-5 text-center'
            style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
          >
            <div
              className='w-16 h-16 rounded-full flex items-center justify-center'
              style={{ background: 'var(--pp-subtle)' }}
            >
              <Link2Off size={24} style={{ color: 'var(--pp-text-subtle)' }} />
            </div>
            <div>
              <h1
                aria-level={1}
                className='text-2xl font-semibold mb-1'
                style={{ color: 'var(--pp-text)', letterSpacing: '-0.02em' }}
              >
                Payment session not found
              </h1>
              <p className='text-sm mt-1.5 leading-relaxed' style={{ color: 'var(--pp-text-muted)' }}>
                This checkout link is invalid or has expired. Request a new link from the app.
              </p>
            </div>
            {sessionId && (
              <div
                className='w-full rounded-lg border text-sm'
                style={{ borderColor: 'var(--pp-border)' }}
              >
                <div className='flex justify-between px-4 py-2.5'>
                  <span style={{ color: 'var(--pp-text-muted)' }}>Reference</span>
                  <span style={{ fontFamily: 'var(--pp-mono)', fontSize: '0.75rem', color: 'var(--pp-text)' }}>
                    {sessionId}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </CheckoutShell>
    );
  }

  if (loadError && !session) {
    return (
      <CheckoutShell>
        <div className='flex-1 flex items-center justify-center p-6'>
          <div
            className='w-full max-w-md rounded-xl border p-8 flex flex-col items-center gap-5 text-center'
            style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
          >
            <div
              className='w-16 h-16 rounded-full flex items-center justify-center'
              style={{ background: 'var(--pp-subtle)' }}
            >
              <Link2Off size={24} style={{ color: 'var(--pp-text-subtle)' }} />
            </div>
            <div>
              <h1
                aria-level={1}
                className='text-2xl font-semibold mb-1'
                style={{ color: 'var(--pp-text)', letterSpacing: '-0.02em' }}
              >
                Could not load this payment
              </h1>
              <p className='text-sm mt-1.5 leading-relaxed' style={{ color: 'var(--pp-text-muted)' }}>
                {loadError}
              </p>
            </div>
          </div>
        </div>
      </CheckoutShell>
    );
  }

  if (!session) {
    return (
      <CheckoutShell>
        <div className='flex-1 flex items-center justify-center p-6'>
          <div
            className='w-full max-w-md rounded-xl border p-8 flex flex-col items-center gap-5 text-center'
            style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
          >
            <Loader2 size={28} className='animate-spin' style={{ color: 'var(--pp-text-muted)' }} />
            <div className='text-sm' style={{ color: 'var(--pp-text-muted)' }}>
              Confirming your payment…
            </div>
          </div>
        </div>
      </CheckoutShell>
    );
  }

  return (
    <CheckoutShell>
      <div className='flex-1 flex items-center justify-center p-6'>
        <div
          className='w-full max-w-md rounded-xl border p-8 flex flex-col items-center gap-5 text-center'
          style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
        >
          {/* Checkmark */}
          <div
            className='w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold'
            style={{
              background: 'var(--pp-settled)',
              animation: '@media (prefers-reduced-motion: no-preference) { scale-in 200ms ease-out }',
            }}
          >
            ✓
          </div>

          <div>
            <h1
              className='text-2xl font-semibold mb-1'
              style={{ color: 'var(--pp-text)', letterSpacing: '-0.02em' }}
            >
              {isComplete ? 'Payment received' : 'Payment confirmed'}
            </h1>
            <div
              className='text-4xl font-semibold tabular-nums mt-2'
              style={{ fontFamily: 'var(--pp-mono)', color: 'var(--pp-text)', letterSpacing: '-0.03em' }}
            >
              ${usdStr}
            </div>
          </div>

          <StatusChip status={isComplete ? 'settled' : 'payment_detected'} />

          {isProcessing && (
            <div
              className='w-full rounded-lg border px-4 py-3 text-sm'
              style={{ background: 'var(--pp-subtle)', borderColor: 'var(--pp-border)', color: 'var(--pp-text-muted)' }}
            >
              {fulfillmentPending(session)
                ? `Finalizing access with ${session?.tenantName ?? 'the app'} now…`
                : fulfillmentFailed(session)
                  ? `Payment received, but ${session?.tenantName ?? 'the app'} has not confirmed access yet.`
                : 'Waiting for Whop to send the signed payment confirmation. This usually takes a few seconds.'}
            </div>
          )}

          {/* Detail rows */}
          {session && (
            <div
              className='w-full rounded-lg border text-sm divide-y'
              style={{ borderColor: 'var(--pp-border)' }}
            >
              {session.chosenRail && (
                <div className='flex justify-between px-4 py-2.5'>
                  <span style={{ color: 'var(--pp-text-muted)' }}>Rail</span>
                  <span className='font-medium uppercase' style={{ color: 'var(--pp-text)' }}>
                    {session.chosenRail}
                  </span>
                </div>
              )}
              {(session as any).txSignature && (
                <div className='flex justify-between px-4 py-2.5'>
                  <span style={{ color: 'var(--pp-text-muted)' }}>Tx</span>
                  <span style={{ fontFamily: 'var(--pp-mono)', fontSize: '0.75rem', color: 'var(--pp-text)' }}>
                    {(session as any).txSignature.slice(0, 16)}…
                  </span>
                </div>
              )}
              <div className='flex justify-between px-4 py-2.5'>
                <span style={{ color: 'var(--pp-text-muted)' }}>Session</span>
                <span style={{ fontFamily: 'var(--pp-mono)', fontSize: '0.75rem', color: 'var(--pp-text)' }}>
                  {session.sessionId}
                </span>
              </div>
              {settledTime && (
                <div className='flex justify-between px-4 py-2.5'>
                  <span style={{ color: 'var(--pp-text-muted)' }}>Settled</span>
                  <span style={{ fontFamily: 'var(--pp-mono)', fontSize: '0.75rem', color: 'var(--pp-text)' }}>
                    {settledTime}
                  </span>
                </div>
              )}
            </div>
          )}

          {shouldRedirect && session?.successRedirectUrl && (
            <div className='flex flex-col items-center gap-2 w-full'>
              <div className='text-sm font-medium' style={{ color: 'var(--pp-text)' }}>
                Redirecting you back now...
              </div>
              <div className='text-xs' style={{ color: 'var(--pp-text-muted)' }}>
                Returning to {session.tenantName ?? 'the app'} in{' '}
                <span style={{ fontFamily: 'var(--pp-mono)', fontWeight: 500 }}>{countdown}s</span>
              </div>
              <a
                href={session.successRedirectUrl}
                className='w-full py-2.5 px-4 rounded text-sm font-semibold text-center text-white'
                style={{ background: 'var(--pp-action)' }}
              >
                Return now
              </a>
            </div>
          )}
        </div>
      </div>
    </CheckoutShell>
  );
};

export default SuccessPage;
